import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Box,
  Typography,
  LinearProgress,
  Stack,
} from "@mui/material";
import {
  AddCircle as AddCircleIcon,
  Edit as EditIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { Controller } from "react-hook-form";
import axiosInstance from "utilities/axios";
import { useAccess } from "hooks/useAccess";
import { useOrgForm } from "hooks/useOrgForm";
import type { OrgFormValues } from "hooks/useOrgForm";

type OrgType = "agency" | "monitoring_company";
type AppType = "universal" | "cgs";

type ActiveAssignment = {
  _id: string;
  application_type: AppType;
  assigned_at: string;
};

type Organization = {
  _id: string;
  name: string;
  identification_code: string;
  org_type: OrgType;
  contact_person?: string;
  phone?: string;
  email?: string;
  is_active: boolean;
  activeAssignments: ActiveAssignment[];
};

type Assignment = {
  _id: string;
  application_type: AppType;
  assigned_at: string;
  ended_at: string | null;
  is_active: boolean;
  notes?: string;
};

const APP_TYPE_LABEL: Record<AppType, string> = {
  universal: "Universal",
  cgs: "CGS",
};


function Organizations() {
  const { canManageOrgs } = useAccess();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>("");

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Organization | null>(null);
  const [saving, setSaving] = useState(false);

  const orgForm = useOrgForm();

  // Confirm toggle dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmOrg, setConfirmOrg] = useState<Organization | null>(null);
  const [toggling, setToggling] = useState(false);

  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyOrg, setHistoryOrg] = useState<Organization | null>(null);
  const [history, setHistory] = useState<Assignment[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterType) params.org_type = filterType;
      const { data } = await axiosInstance.get("/organizations", { params });
      setOrganizations(data);
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  function openCreate() {
    setEditTarget(null);
    orgForm.reset({ name: "", identification_code: "", org_type: "monitoring_company", contact_person: "", phone: "", email: "" });
    setDialogOpen(true);
  }

  function openEdit(org: Organization) {
    setEditTarget(org);
    orgForm.reset({
      name: org.name,
      identification_code: org.identification_code,
      org_type: org.org_type,
      contact_person: org.contact_person || "",
      phone: org.phone || "",
      email: org.email || "",
    });
    setDialogOpen(true);
  }

  async function handleSave(values: OrgFormValues) {
    setSaving(true);
    try {
      if (editTarget) {
        await axiosInstance.put(`/organizations/${editTarget._id}`, values);
      } else {
        await axiosInstance.post("/organizations", values);
      }
      setDialogOpen(false);
      fetchOrgs();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setSaving(false);
    }
  }

  function openConfirmToggle(org: Organization) {
    setConfirmOrg(org);
    setConfirmOpen(true);
  }

  async function handleConfirmToggle() {
    if (!confirmOrg) return;
    setToggling(true);
    try {
      await axiosInstance.put(`/organizations/${confirmOrg._id}`, { ...confirmOrg, is_active: !confirmOrg.is_active });
      setConfirmOpen(false);
      setConfirmOrg(null);
      fetchOrgs();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setToggling(false);
    }
  }

  async function openHistory(org: Organization) {
    setHistoryOrg(org);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    setHistory([]);
    try {
      const { data } = await axiosInstance.get(`/organizations/${org._id}/assignments`);
      setHistory(data);
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setHistoryLoading(false);
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title="მონიტორინგის კომპანიები"
          action={
            canManageOrgs ? (
              <Button variant="contained" startIcon={<AddCircleIcon />} onClick={openCreate}>
                დამატება
              </Button>
            ) : null
          }
        />
        <CardContent>
          <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>ტიპი</InputLabel>
              <Select value={filterType} label="ტიპი" onChange={(e) => setFilterType(e.target.value as string)}>
                <MenuItem value="">ყველა</MenuItem>
                <MenuItem value="monitoring_company">მონიტ. კომპანია</MenuItem>
                <MenuItem value="agency">სააგენტო</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading && <LinearProgress sx={{ mb: 1 }} />}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>დასახელება</TableCell>
                  <TableCell>საიდ. კოდი</TableCell>
                  <TableCell>ტიპი</TableCell>
                  <TableCell>სტატუსი</TableCell>
                  <TableCell>მიმდინარე მონიტორინგის კომპანია</TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => (
                  <TableRow key={org._id} hover>
                    <TableCell>{org.name}</TableCell>
                    <TableCell>{org.identification_code}</TableCell>
                    <TableCell>{org.org_type === "agency" ? "სააგენტო" : "მონიტ. კომპ."}</TableCell>
                    <TableCell>
                      <Chip
                        label={org.is_active ? "აქტიური" : "დეაქტივირებული"}
                        color={org.is_active ? "success" : "error"}
                        size="small"
                        variant="outlined"
                        onClick={canManageOrgs ? () => openConfirmToggle(org) : undefined}
                        sx={canManageOrgs ? { cursor: "pointer" } : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      {org.activeAssignments?.length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {org.activeAssignments.map((a) => (
                            <Chip
                              key={a._id}
                              label={APP_TYPE_LABEL[a.application_type as AppType] ?? a.application_type}
                              size="small"
                              color="success"
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {canManageOrgs && (
                        <Tooltip title="რედაქტირება">
                          <IconButton size="small" onClick={() => openEdit(org)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="ისტორია">
                        <IconButton size="small" onClick={() => openHistory(org)}><HistoryIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && organizations.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary">ჩანაწერები ვერ მოიძებნა</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={orgForm.handleSubmit(handleSave)}>
          <DialogTitle>{editTarget ? "რედაქტირება" : "ახალი ორგანიზაცია"}</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
            <Controller
              name="name"
              control={orgForm.control}
              rules={{ required: "სავალდებულო ველი" }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="დასახელება" fullWidth error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
            <Controller
              name="identification_code"
              control={orgForm.control}
              rules={{ required: "სავალდებულო ველი" }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="საიდენტ. კოდი" fullWidth error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
            <Controller
              name="org_type"
              control={orgForm.control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>ტიპი</InputLabel>
                  <Select {...field} label="ტიპი">
                    <MenuItem value="monitoring_company">მონიტ. კომპანია</MenuItem>
                    <MenuItem value="agency">სააგენტო</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="contact_person"
              control={orgForm.control}
              render={({ field }) => <TextField {...field} label="საკონტაქტო პირი" fullWidth />}
            />
            <Controller
              name="phone"
              control={orgForm.control}
              render={({ field }) => <TextField {...field} label="ტელეფონი" fullWidth />}
            />
            <Controller
              name="email"
              control={orgForm.control}
              render={({ field }) => <TextField {...field} label="ელ-ფოსტა" fullWidth />}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>გაუქმება</Button>
            <Button type="submit" variant="contained" disabled={saving}>შენახვა</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirm toggle active dialog */}
      <Dialog open={confirmOpen} onClose={() => { setConfirmOpen(false); setConfirmOrg(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>დადასტურება</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            დარწმუნებული ხართ, რომ გსურთ ორგანიზაციის{" "}
            <strong>{confirmOrg?.name}</strong>{" "}
            {confirmOrg?.is_active ? "დეაქტივაცია" : "გააქტიურება"}?
          </Typography>
          {confirmOrg?.is_active && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              ორგანიზაციის დეაქტივაციისას ყველა წევრი ავტომატურად დეაქტივირდება.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOpen(false); setConfirmOrg(null); }}>გაუქმება</Button>
          <Button
            variant="contained"
            color={confirmOrg?.is_active ? "error" : "success"}
            disabled={toggling}
            onClick={handleConfirmToggle}
          >
            {confirmOrg?.is_active ? "დეაქტივაცია" : "გააქტიურება"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{historyOrg?.name} — მონიტორინგის პერიოდების ისტორია</DialogTitle>
        <DialogContent>
          {historyLoading && <LinearProgress />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ტიპი</TableCell>
                  <TableCell>დაწყება</TableCell>
                  <TableCell>დასრულება</TableCell>
                  <TableCell>სტატუსი</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((a) => (
                  <TableRow key={a._id}>
                    <TableCell>{APP_TYPE_LABEL[a.application_type] ?? a.application_type}</TableCell>
                    <TableCell>{new Date(a.assigned_at).toLocaleDateString("ka-GE")}</TableCell>
                    <TableCell>{a.ended_at ? new Date(a.ended_at).toLocaleDateString("ka-GE") : "—"}</TableCell>
                    <TableCell>
                      <Chip label={a.is_active ? "აქტიური" : "დასრულებული"} color={a.is_active ? "success" : "default"} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
                {!historyLoading && history.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center"><Typography variant="body2" color="text.secondary">ისტორია არ მოიძებნა</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>დახურვა</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Organizations;
